import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { 
  BookOpen, BarChart2, Globe, Sparkles, Brain, Target, 
  Zap, Award, Users, ArrowRight, Check, FileText, FolderKanban,
  GraduationCap, Clock, TrendingUp, Shield, MessageSquare,
  Smartphone, Star, PenTool,
  Headphones, CheckCircle2
} from 'lucide-react';

// ─── Animated counter hook ───────────────────────────────────
const useCountUp = (end: number, duration: number = 2000, startOnView: boolean = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!startOnView) setHasStarted(true);
  }, [startOnView]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !hasStarted) setHasStarted(true); },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;
    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration, hasStarted]);

  return { count, ref };
};

// ─── Floating decoration ─────────────────────────────────────
const FloatingElement: React.FC<{ 
  children: React.ReactNode; 
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className = '' }) => (
  <div className={`animate-float ${className}`} style={{ animationDelay: `${delay}s` }}>
    {children}
  </div>
);

// ─── Stat counter ────────────────────────────────────────────
const StatItem: React.FC<{
  value: number;
  suffix: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  delay: number;
}> = ({ value, suffix, label, icon: Icon, delay }) => {
  const { count, ref } = useCountUp(value);
  return (
    <div ref={ref} className="text-center group" style={{ animationDelay: `${delay}s` }}>
      <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
        <Icon className="w-7 h-7 text-cyan-400" />
      </div>
      <div className="text-3xl md:text-4xl font-black text-white mb-1">{count}{suffix}</div>
      <div className="text-slate-400 text-sm font-medium">{label}</div>
    </div>
  );
};

// ─── Bento feature card (large) ──────────────────────────────
const BentoLarge: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  bullets: string[];
  gradient: string;
  accentColor: string;
}> = ({ icon: Icon, title, description, bullets, gradient, accentColor }) => (
  <div className={`group relative p-8 md:p-10 rounded-3xl border border-slate-700/50 
                   bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-sm
                   hover:border-${accentColor}-500/40 transition-all duration-500 hover:-translate-y-1 
                   hover:shadow-2xl hover:shadow-${accentColor}-500/10 overflow-hidden`}>
    {/* Glow */}
    <div className={`absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br ${gradient} rounded-full blur-3xl opacity-0 group-hover:opacity-20 transition-opacity duration-700`} />
    <div className="relative z-10">
      <div className={`w-14 h-14 bg-gradient-to-br ${gradient} rounded-2xl flex items-center justify-center mb-6 
                       group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
      <h3 className="text-2xl font-bold mb-3 text-white">{title}</h3>
      <p className="text-slate-400 leading-relaxed mb-5">{description}</p>
      <ul className="space-y-2">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-slate-400 text-sm">
            <CheckCircle2 className={`w-4 h-4 text-${accentColor}-400 mt-0.5 shrink-0`} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

// ─── Bento feature card (small) ──────────────────────────────
const BentoSmall: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  gradient: string;
}> = ({ icon: Icon, title, description, gradient }) => (
  <div className="group p-6 rounded-2xl border border-slate-700/50 bg-gradient-to-br from-slate-800/50 to-slate-900/50 
                  backdrop-blur-sm hover:border-cyan-500/30 transition-all duration-500 hover:-translate-y-1 
                  hover:shadow-xl hover:shadow-cyan-500/5">
    <div className={`w-12 h-12 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center mb-4 
                     group-hover:scale-110 transition-all duration-300 shadow-md`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <h3 className="text-lg font-bold mb-2 text-white group-hover:text-cyan-400 transition-colors">{title}</h3>
    <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
  </div>
);

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Home Page
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
const Home: React.FC = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' }, delay: 0.2 });
      tl.from('.hero-badge', { y: 30, opacity: 0, duration: 0.8 })
        .from('.hero-title-1', { y: 40, opacity: 0, duration: 0.8 }, '-=0.4')
        .from('.hero-title-2', { y: 40, opacity: 0, duration: 0.8 }, '-=0.5')
        .from('.hero-desc', { y: 30, opacity: 0, duration: 0.7 }, '-=0.4')
        .from('.hero-ctas', { y: 30, opacity: 0, duration: 0.7 }, '-=0.3')
        .from('.hero-trust', { y: 20, opacity: 0, duration: 0.6 }, '-=0.3')
        .from('.hero-mockup', { y: 60, opacity: 0, duration: 1, ease: 'power2.out' }, '-=0.8');
    }, mainRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const els = mainRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    if (!els?.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: 10000, suffix: '+', label: 'Students Helped', icon: Users },
    { value: 95, suffix: '%', label: 'Satisfaction Rate', icon: Award },
    { value: 50, suffix: '+', label: 'Subjects Covered', icon: BookOpen },
    { value: 24, suffix: '/7', label: 'Always Available', icon: Clock },
  ];

  const howItWorks = [
    { step: '01', title: 'Sign Up', description: 'Create your account in seconds — free to start.', icon: Smartphone },
    { step: '02', title: 'Pick Your Subjects', description: 'Choose from 50+ ZIMSEC-aligned subjects. Most subjects are $3/month, while English and A-Level subjects are $5/month.', icon: BookOpen },
    { step: '03', title: 'Learn Your Way', description: 'Chat with AI, submit homework photos, run projects, or revise for exams — all in one place.', icon: Brain },
    { step: '04', title: 'Track Progress', description: 'See mastery levels, grades over time, and get personalised recommendations.', icon: TrendingUp },
  ];

  return (
    <div ref={mainRef} className="min-h-screen bg-[#0a0f1a] text-gray-100 overflow-x-hidden">
      {/* ── Noise texture overlay ────────────────────────────── */}
      <svg aria-hidden="true" className="absolute w-0 h-0 overflow-hidden">
        <filter id="noiseFilter">
          <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
        </filter>
      </svg>
      <div className="fixed inset-0 w-full h-full pointer-events-none z-[100] opacity-[0.035]"
           style={{ filter: 'url(#noiseFilter)' }} />

      {/* ── Animated background orbs ─────────────────────────── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-[500px] h-[500px] bg-cyan-500/8 rounded-full blur-[120px] animate-orbit-1" />
        <div className="absolute bottom-1/4 -right-1/4 w-[500px] h-[500px] bg-blue-500/8 rounded-full blur-[120px] animate-orbit-2" />
        <div className="absolute top-1/2 left-1/2 w-[700px] h-[700px] bg-purple-500/5 rounded-full blur-[160px] animate-orbit-3" />
      </div>

      {/* ── Header ──────────────────────────────────────────── */}
      <header className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#0a0f1a]/70 backdrop-blur-xl border-b border-slate-700/50 shadow-lg shadow-black/10'
          : 'bg-transparent border-b border-transparent'
      }`}>
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Shasha
            </h1>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium">Features</a>
            <a href="#how-it-works" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium">How It Works</a>
            <a href="#stats" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium">Impact</a>
            <a href="#pricing" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium">Pricing</a>
          </nav>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="text-slate-300 hover:text-cyan-400 transition-colors font-medium text-sm"
            >
              Sign In
            </button>
            <button
              onClick={() => navigate('/login')}
              className="magnetic-btn group relative overflow-hidden bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold py-2.5 px-6 rounded-full text-sm"
            >
              <span className="relative z-10">Get Started</span>
              <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-600 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]" />
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero Section ────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center pt-24 pb-12">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div>
              <div className="hero-badge inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-8">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-400 font-medium">AI-Powered Education for Zimbabwe</span>
              </div>

              <h1 className="text-5xl md:text-6xl xl:text-7xl font-black mb-6 leading-[1.1]">
                <span className="hero-title-1 block bg-gradient-to-r from-white via-slate-200 to-slate-400 bg-clip-text text-transparent">
                  Your Personal
                </span>
                <span className="hero-title-2 block bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent animate-gradient">
                  AI Tutor
                </span>
              </h1>

              <p className="hero-desc text-lg md:text-xl text-slate-400 mb-8 leading-relaxed max-w-xl">
                Shasha coaches you through lessons, homework, exams, and school projects — 
                aligned to the <span className="text-white font-medium">ZIMSEC</span> curriculum. 
                Available 24/7 on web and WhatsApp.
              </p>

              <div className="hero-ctas flex flex-col sm:flex-row gap-4 mb-10">
                <button
                  onClick={() => navigate('/login')}
                  className="magnetic-btn group relative overflow-hidden bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold py-4 px-8 rounded-full 
                            hover:shadow-2xl hover:shadow-cyan-500/30 flex items-center justify-center gap-2 text-lg"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Start Learning Free
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-600 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]" />
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="magnetic-btn group relative overflow-hidden border border-slate-600 hover:border-cyan-500/50 text-slate-300 hover:text-white 
                            font-semibold py-4 px-8 rounded-full 
                            flex items-center justify-center gap-2 text-lg"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-green-400" />
                    Try on WhatsApp
                  </span>
                  <span className="absolute inset-0 bg-slate-800/80 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]" />
                </button>
              </div>

              <div className="hero-trust flex flex-wrap gap-6 text-slate-500 text-sm">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400" />
                  <span>EcoCash & cards accepted</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400" />
                  <span>Most subjects $3/month, English & A-Level $5/month</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-cyan-400" />
                  <span>ZIMSEC curriculum aligned</span>
                </div>
              </div>
            </div>

            {/* Right: Product preview mockup */}
            <div className="hero-mockup hidden lg:block">
              <div className="relative">
                {/* Glow behind card */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-blue-500/10 to-purple-500/20 rounded-3xl blur-2xl" />
                
                {/* Chat mockup card */}
                <div className="relative bg-slate-900/90 border border-slate-700/60 rounded-3xl p-6 shadow-2xl">
                  {/* Toolbar */}
                  <div className="flex items-center gap-3 pb-4 border-b border-slate-700/50 mb-4">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500/60" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                      <div className="w-3 h-3 rounded-full bg-green-500/60" />
                    </div>
                    <div className="flex-1 text-center text-xs text-slate-500 font-medium">Chemistry &middot; Form 3</div>
                  </div>

                  {/* Messages */}
                  <div className="space-y-4 mb-4">
                    <div className="flex justify-end">
                      <div className="bg-cyan-500/20 border border-cyan-500/20 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
                        <p className="text-sm text-slate-200">I want to make homemade soap from used cooking oil. Can you help me with the project?</p>
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-slate-800/80 border border-slate-700/50 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
                        <p className="text-sm text-slate-300 mb-2">Great topic! Making soap from cooking oil is a practical <span className="text-cyan-400 font-medium">saponification</span> project. Let me help you plan it.</p>
                        <p className="text-sm text-slate-400">Here are <span className="text-white font-medium">4 investigation directions</span> you could take:</p>
                        <div className="mt-2 space-y-1.5">
                          <div className="flex items-start gap-2 text-xs text-slate-400">
                            <span className="text-cyan-400 font-bold">1.</span>
                            <span>Fair test: vary one factor (oil type, NaOH concentration...)</span>
                          </div>
                          <div className="flex items-start gap-2 text-xs text-slate-400">
                            <span className="text-cyan-400 font-bold">2.</span>
                            <span>Cost analysis: compare homemade vs commercial soap</span>
                          </div>
                          <div className="flex items-start gap-2 text-xs text-slate-400">
                            <span className="text-cyan-400 font-bold">3.</span>
                            <span>Community impact: survey households for used oil waste</span>
                          </div>
                          <div className="flex items-start gap-2 text-xs text-slate-400">
                            <span className="text-cyan-400 font-bold">4.</span>
                            <span>Quality testing: pH levels and lather across recipes</span>
                          </div>
                        </div>
                        <p className="text-sm text-slate-400 mt-3">Which direction interests you most?</p>
                      </div>
                    </div>
                  </div>

                  {/* Input bar */}
                  <div className="flex items-center gap-3 bg-slate-800/60 rounded-xl px-4 py-3 border border-slate-700/40">
                    <PenTool className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-500 flex-1">Type your message...</span>
                    <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>

                {/* Floating badges */}
                <FloatingElement delay={0.5} className="absolute -top-4 -right-4">
                  <div className="bg-green-500/20 border border-green-500/30 rounded-xl px-3 py-2 backdrop-blur-md flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-green-400" />
                    <span className="text-xs text-green-400 font-medium">WhatsApp Ready</span>
                  </div>
                </FloatingElement>

                <FloatingElement delay={1} className="absolute -bottom-4 -left-4">
                  <div className="bg-purple-500/20 border border-purple-500/30 rounded-xl px-3 py-2 backdrop-blur-md flex items-center gap-2">
                    <FolderKanban className="w-4 h-4 text-purple-400" />
                    <span className="text-xs text-purple-400 font-medium">SBP 7-Stage Coach</span>
                  </div>
                </FloatingElement>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
          <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-cyan-400 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* ── Stats Section ───────────────────────────────────── */}
      <section id="stats" className="py-16 relative">
        <div className="container mx-auto px-6">
          <div className="reveal bg-gradient-to-r from-slate-800/40 via-slate-800/60 to-slate-800/40 border border-slate-700/40 rounded-3xl p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="reveal" style={{ transitionDelay: `${index * 0.12}s` }}>
                  <StatItem value={stat.value} suffix={stat.suffix} label={stat.label} icon={stat.icon} delay={index * 0.1} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Features Section (Bento Grid) ───────────────────── */}
      <section id="features" className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="reveal text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-400 font-medium">Everything In One Platform</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
                Built for How Zimbabwe
              </span>
              <br />
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Students Actually Learn
              </span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              From daily lessons to 50-mark school projects — Shasha covers 
              every part of your academic journey.
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="reveal" style={{ transitionDelay: '0s' }}>
              <BentoLarge
                icon={Brain}
                title="AI Tutoring"
                description="A personal Socratic tutor that adapts to your level. Step-by-step explanations, hints when you're stuck, and auto-difficulty adjustment."
                bullets={[
                  "One question at a time — guides you, never gives answers away",
                  "KRCP difficulty adapts: easier when stuck, harder when ready",
                  "Covers 50+ subjects: Maths, Sciences, Commerce, Languages",
                  "Works for ECD through A-Level"
                ]}
                gradient="from-cyan-500 to-blue-500"
                accentColor="cyan"
              />
            </div>
            <div className="reveal" style={{ transitionDelay: '0.1s' }}>
              <BentoLarge
                icon={FolderKanban}
                title="School-Based Projects"
                description="Guided coaching through all 7 stages of the ZIMSEC SBP framework, with the 50-mark rubric baked in. Plan, investigate, build, present."
                bullets={[
                  "Topic picker: 4 safe, feasible project directions",
                  "Stage 1-7 templates with fill-in-the-blank scaffolds",
                  "Evidence checklists and cover page generators",
                  "Grading mode: get a practice mark before submission"
                ]}
                gradient="from-orange-500 to-yellow-500"
                accentColor="orange"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="reveal" style={{ transitionDelay: '0.2s' }}>
              <BentoSmall
                icon={FileText}
                title="Homework Help"
                description="Snap a photo of your homework or type your question. Step-by-step guidance, not just answers."
                gradient="from-emerald-500 to-cyan-500"
              />
            </div>
            <div className="reveal" style={{ transitionDelay: '0.3s' }}>
              <BentoSmall
                icon={Target}
                title="Exam Revision"
                description="Targeted ZIMSEC revision with adaptive practice questions, pacing plans, and readiness checks."
                gradient="from-purple-500 to-pink-500"
              />
            </div>
            <div className="reveal" style={{ transitionDelay: '0.4s' }}>
              <BentoSmall
                icon={Shield}
                title="Remediation"
                description="When you're stuck, Shasha detects missing prerequisites and loops back to fill the gaps before moving on."
                gradient="from-blue-500 to-purple-500"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="reveal" style={{ transitionDelay: '0.5s' }}>
              <BentoSmall
                icon={BarChart2}
                title="Progress Tracking"
                description="See mastery levels per topic. Teachers and parents get dashboards to monitor student performance."
                gradient="from-pink-500 to-orange-500"
              />
            </div>
            <div className="reveal" style={{ transitionDelay: '0.6s' }}>
              <BentoSmall
                icon={MessageSquare}
                title="WhatsApp Access"
                description="No app download needed. Chat with Shasha directly on WhatsApp — same AI, same subjects, any phone."
                gradient="from-green-500 to-emerald-500"
              />
            </div>
            <div className="reveal" style={{ transitionDelay: '0.7s' }}>
              <BentoSmall
                icon={Headphones}
                title="Voice & Image Input"
                description="Record voice messages or send images of diagrams, textbooks, and worksheets. AI reads and responds."
                gradient="from-indigo-500 to-blue-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 relative bg-gradient-to-b from-transparent via-slate-900/50 to-transparent">
        <div className="container mx-auto px-6">
          <div className="reveal text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
              <Zap className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-400 font-medium">Quick Start</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
                Up and Running in
              </span>
              {' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Minutes
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {howItWorks.map((item, index) => {
              const StepIcon = item.icon;
              return (
                <div key={index} className="reveal relative group" style={{ transitionDelay: `${index * 0.12}s` }}>
                  {/* Connector */}
                  {index < howItWorks.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-[60%] w-full h-px">
                      <div className="h-full bg-gradient-to-r from-cyan-500/40 to-transparent" />
                    </div>
                  )}
                  <div className="text-center p-6">
                    <div className="w-20 h-20 mx-auto mb-5 relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl rotate-6 group-hover:rotate-12 transition-transform" />
                      <div className="absolute inset-0 bg-slate-900 rounded-2xl flex items-center justify-center border border-slate-700/50">
                        <StepIcon className="w-8 h-8 text-cyan-400" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-7 h-7 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center text-xs font-black text-white shadow-lg">
                        {item.step}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold mb-2 text-white">{item.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Subject Showcase ────────────────────────────────── */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="reveal text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <BookOpen className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-emerald-400 font-medium">Curriculum Coverage</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
                Every Subject,
              </span>
              {' '}
              <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
                Every Level
              </span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              From ECD through A-Level — ZIMSEC aligned and Heritage-Based Curriculum.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 max-w-5xl mx-auto">
            {[
              { name: 'Mathematics', icon: '📐' },
              { name: 'Physics', icon: '⚡' },
              { name: 'Chemistry', icon: '🧪' },
              { name: 'Biology', icon: '🧬' },
              { name: 'English', icon: '📖' },
              { name: 'Combined Science', icon: '🔬' },
              { name: 'Geography', icon: '🌍' },
              { name: 'History', icon: '📜' },
              { name: 'Accounts', icon: '📊' },
              { name: 'Economics', icon: '💹' },
              { name: 'Agriculture', icon: '🌱' },
              { name: 'Computer Science', icon: '💻' },
            ].map((subj, i) => (
              <div key={i} className="reveal p-4 rounded-xl border border-slate-700/40 bg-slate-800/30 hover:border-cyan-500/30 
                                     hover:bg-slate-800/50 text-center group cursor-default"
                   style={{ transitionDelay: `${i * 0.04}s` }}>
                <div className="text-2xl mb-2">{subj.icon}</div>
                <div className="text-xs font-medium text-slate-400 group-hover:text-white transition-colors">{subj.name}</div>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 text-sm mt-6">...and 40+ more subjects</p>
        </div>
      </section>

      {/* ── Impact / Education Gap ──────────────────────────── */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="reveal text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/20 rounded-full mb-6">
                <Globe className="w-4 h-4 text-orange-400" />
                <span className="text-sm text-orange-400 font-medium">Why This Matters</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
                  Bridging the
                </span>
                {' '}
                <span className="bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent">
                  Education Gap
                </span>
              </h2>
              <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                Quality education should not be a privilege. These numbers show why AI tutoring matters for Zimbabwe.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="reveal-left p-8 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl border border-slate-700/50 hover:border-cyan-500/30 group">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <div className="text-cyan-400 font-bold text-lg">Zimbabwe Grade 7</div>
                    <div className="text-slate-500 text-xs">2023 ZIMSEC pass rate</div>
                  </div>
                </div>
                <div className="text-5xl font-black text-white mb-3 group-hover:scale-105 transition-transform origin-left">45.57%</div>
                <p className="text-slate-400 text-sm">Compared to 74% in UK Key Stage 2 — a gap we can close with personalised AI support.</p>
                <div className="mt-4 h-2.5 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full w-[45%] bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-1000" />
                </div>
              </div>
              
              <div className="reveal-right p-8 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl border border-slate-700/50 hover:border-orange-500/30 group" style={{ transitionDelay: '0.15s' }}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center">
                    <BarChart2 className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <div className="text-orange-400 font-bold text-lg">O-Level vs GCSE</div>
                    <div className="text-slate-500 text-xs">Form 4 pass rate comparison</div>
                  </div>
                </div>
                <div className="text-5xl font-black text-white mb-3 group-hover:scale-105 transition-transform origin-left">29.41%</div>
                <p className="text-slate-400 text-sm">Zimbabwe O-Level vs UK's 67.8% GCSE rate. Every student deserves a fighting chance.</p>
                <div className="mt-4 h-2.5 bg-slate-700 rounded-full overflow-hidden">
                  <div className="h-full w-[29%] bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full transition-all duration-1000" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing Teaser ──────────────────────────────────── */}
      <section id="pricing" className="py-24 relative bg-gradient-to-b from-transparent via-slate-900/50 to-transparent">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full mb-6">
                <Star className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-400 font-medium">Simple Pricing</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">
                  Affordable for
                </span>
                {' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Every Family
                </span>
              </h2>
            </div>

            <div className="reveal-scale bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700/50 rounded-3xl p-8 md:p-12 mt-10">
              <div className="flex items-baseline justify-center gap-1 mb-2">
                <span className="text-6xl font-black text-white">$3</span>
                <span className="text-slate-400 text-lg">+</span>
              </div>
              <p className="text-slate-400 mb-8">per subject for standard subjects. English and all A-Level subjects are $5/month.</p>
              
              <div className="grid sm:grid-cols-2 gap-4 text-left max-w-md mx-auto mb-10">
                {[
                  'Unlimited AI tutoring sessions',
                  'Homework help (text + images)',
                  'Exam revision & mock practice',
                  'Project coaching (all 7 stages)',
                  'Progress tracking dashboard',
                  'WhatsApp access included',
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-cyan-400 shrink-0" />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/login')}
                className="magnetic-btn group relative overflow-hidden bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold py-4 px-10 rounded-full 
                          hover:shadow-2xl hover:shadow-cyan-500/30 
                          flex items-center gap-2 text-lg mx-auto"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Get Started
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-600 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────── */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="reveal-scale max-w-4xl mx-auto text-center p-12 bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-purple-500/10 rounded-3xl border border-cyan-500/20 relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-cyan-500/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl" />
            </div>
            
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
                  Ready to
                </span>
                {' '}
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Level Up?
                </span>
              </h2>
              <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
                Join thousands of Zimbabwean students already using AI to prepare for exams, 
                complete projects, and master their subjects.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/login')}
                  className="magnetic-btn group relative overflow-hidden bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold py-4 px-10 rounded-full 
                            hover:shadow-2xl hover:shadow-cyan-500/30 
                            flex items-center justify-center gap-2 text-lg"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Start Learning Free
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-600 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]" />
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="magnetic-btn group relative overflow-hidden border border-slate-600 hover:border-green-500/50 text-slate-300 hover:text-white 
                            font-semibold py-4 px-10 rounded-full 
                            flex items-center justify-center gap-2 text-lg"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-green-400" />
                    WhatsApp
                  </span>
                  <span className="absolute inset-0 bg-slate-800/80 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)]" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────── */}
      <footer className="border-t border-slate-800 py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-10">
            {/* Brand */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/20">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <span className="text-lg font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Shasha
                </span>
              </div>
              <p className="text-slate-500 text-sm leading-relaxed">
                AI-powered education for Zimbabwe learners. 
                Aligned to the ZIMSEC curriculum.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm">Product</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#features" className="hover:text-cyan-400 transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-cyan-400 transition-colors">Pricing</a></li>
                <li><a href="#how-it-works" className="hover:text-cyan-400 transition-colors">How It Works</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm">Support</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><button className="hover:text-cyan-400 transition-colors">Contact Us</button></li>
                <li><button className="hover:text-cyan-400 transition-colors">FAQ</button></li>
                <li><button className="hover:text-cyan-400 transition-colors">WhatsApp Help</button></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm">Legal</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><button className="hover:text-cyan-400 transition-colors">Privacy Policy</button></li>
                <li><button className="hover:text-cyan-400 transition-colors">Terms of Service</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Shield className="w-4 h-4" />
              <span>&copy; 2026 Shasha. Empowering learners across Zimbabwe.</span>
            </div>
            <div className="flex items-center gap-4 text-slate-500 text-sm">
              <span>Accepts:</span>
              <span className="text-slate-400 font-medium">EcoCash</span>
              <span className="text-slate-600">&middot;</span>
              <span className="text-slate-400 font-medium">Innbucks</span>
              <span className="text-slate-600">&middot;</span>
              <span className="text-slate-400 font-medium">Visa/Mastercard</span>
            </div>
          </div>
        </div>
      </footer>

      {/* ── CSS animations ──────────────────────────────────── */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes orbit1 {
          0%   { transform: translate(0, 0) scale(1); }
          25%  { transform: translate(60px, -40px) scale(1.05); }
          50%  { transform: translate(20px, 50px) scale(0.95); }
          75%  { transform: translate(-50px, 20px) scale(1.03); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes orbit2 {
          0%   { transform: translate(0, 0) scale(1); }
          25%  { transform: translate(-50px, 30px) scale(0.97); }
          50%  { transform: translate(-20px, -60px) scale(1.04); }
          75%  { transform: translate(40px, -20px) scale(0.98); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes orbit3 {
          0%   { transform: translate(-50%, -50%) scale(1); }
          33%  { transform: translate(calc(-50% + 30px), calc(-50% - 40px)) scale(1.03); }
          66%  { transform: translate(calc(-50% - 40px), calc(-50% + 30px)) scale(0.97); }
          100% { transform: translate(-50%, -50%) scale(1); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 4s ease infinite;
        }
        .animate-orbit-1 {
          animation: orbit1 20s ease-in-out infinite;
        }
        .animate-orbit-2 {
          animation: orbit2 25s ease-in-out infinite;
        }
        .animate-orbit-3 {
          animation: orbit3 30s ease-in-out infinite;
        }
        .magnetic-btn {
          transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                      box-shadow 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .magnetic-btn:hover {
          transform: scale(1.03) translateY(-2px);
        }

        .reveal {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal.in-view {
          opacity: 1;
          transform: none;
        }
        .reveal-left {
          opacity: 0;
          transform: translateX(-40px);
          transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal-right {
          opacity: 0;
          transform: translateX(40px);
          transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal-left.in-view,
        .reveal-right.in-view {
          opacity: 1;
          transform: none;
        }
        .reveal-scale {
          opacity: 0;
          transform: scale(0.95) translateY(20px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal-scale.in-view {
          opacity: 1;
          transform: none;
        }
      `}</style>
    </div>
  );
};

export default Home;
